import {isUndefined} from 'lodash';
import React, {FC, ReactNode, ReactText, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {UserTheme, useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsTableMetric,
  AnalyticsTableMetricColumn,
  AnalyticsTableMetricRow,
  AnalyticsTableMetricsEnum,
  isNumericDataPoint,
  isNumericDataPointType,
  isResourceDataPoint,
} from '../../../../models/api/analyticsMetricsModel';
import {setMetricDetailsAction} from '../../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {
  getAnalyticsSelectedMetrics,
  makeGetAnalyticsMetric,
} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {SeparatorsEnum} from '../../../helpers/analyticsExportHelpers';
import {
  getDonutArcIndexColor,
  getMetricRenderer,
  getVariationConfig,
  renderFormattedMetricData,
} from '../../../helpers/analyticsFormatHelpers';
import {
  ContributingSegmentsOptionsEnum,
  ReportFilterOptionsEnum,
} from '../../../helpers/analyticsReportHelpers';
import {useAnalyticsUpdateSelectedMetrics} from '../../../helpers/hooks/useAnalyticsUpdateSelectedMetrics';
import {AnalyticsDataPointValueType, computeCsvQuote} from '../../exports/helpers/metricExports';
import {defaultErrorStateRenderer, ErrorStateRenderer} from '../charts/common/metricErrorState';
import {defaultEmptyStateRenderer, isEmptyMetric} from '../charts/emptyState';
import {ChartExportViewDialog} from '../export/chartExportViewDialog';
import {ReportElementProps, ReportElementWrapper, ReportElementWrapperProps} from '../reportElement';
import {ReportListAccessorDropdown} from './reportListAccessorDropdown';
import {ReportListItem} from './reportListItem';

type RowFilter = (
  row: AnalyticsTableMetricRow,
  columns: ReadonlyArray<AnalyticsTableMetricColumn>,
  valueAccessor: string,
) => boolean;

/*
 * Constants.
 */

const MAX_ITEMS_IN_TOPS = 4;

const mockData: Array<AnalyticsReportListItem> = Array.from(Array(5), () => ({
  label: null,
  value: null,
}));

/*
 * Props.
 */

export interface AnalyticsReportListItem {
  resourceId?: ReactText;
  label: ReactNode;
  value: ReactNode;
  variation?: number;
  formattedVariation?: ReactNode;
  labelCircleColor?: string;
  icon?: IconName;
  color?: string;
}

interface ReportListProps extends ReportElementWrapperProps, ReportElementProps<AnalyticsTableMetricsEnum> {
  labelAccessor: string;
  valueAccessors: ReadonlyArray<string>;
  shouldDisplayTops?: boolean;
  hoveredItemId?: string;
  labelCircleColor?: PaletteColorsEnum;
  exportTitle?: string;
  // Handlers for the export state - required if the component isExportable
  shouldShowExportDialog?: boolean;
  toggleExportDialog?: Handler;
  rowFilter?: RowFilter;
  onItemHover?: HandlerOf<ReactText | undefined>;
  onItemClick?: (item: AnalyticsReportListItem, index: number) => void;
  renderErrorState?: ErrorStateRenderer;
}

/*
 * Styles.
 */

const StyledItemsWrapper = styled.div`
  margin-top: 16px;
`;

const StyledSectionTitle = styled.div`
  ${flexCenter}

  height: 30px;

  font-size: ${fontSizes.verySmall};
  line-height: ${fontSizes.large};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const ReportList: FC<ReportListProps> = ({
  reportSectionId,
  title,
  renderEmptyState = defaultEmptyStateRenderer,
  renderErrorState = defaultErrorStateRenderer,
  infoTooltip,
  isExportable,
  width,
  isLoading,
  metric,
  labelAccessor,
  valueAccessors,
  shouldDisplayTops,
  hoveredItemId,
  labelCircleColor,
  shouldShowExportDialog,
  toggleExportDialog,
  rowFilter,
  onItemClick,
  onItemHover,
  exportTitle,
}) => {
  const dispatch = useAppDispatch();
  const theme = useUserTheme();

  const {updateSelectedMetrics} = useAnalyticsUpdateSelectedMetrics();

  const [metricData, metricError] = useAppSelector(makeGetAnalyticsMetric(metric));
  const selectedMetrics = useAppSelector(getAnalyticsSelectedMetrics);

  const defaultSelectedAccessor = useMemo(
    () => (valueAccessors.length > 0 ? valueAccessors[0] : undefined),
    [valueAccessors],
  );

  const [selectedValueAccessor, setSelectedValueAccessor] = useState(defaultSelectedAccessor);

  // This will trigger when we select a view or change the selected metric.
  useEffect(() => {
    if (selectedMetrics[reportSectionId]) {
      setSelectedValueAccessor(selectedMetrics[reportSectionId][0]);
    } else {
      setSelectedValueAccessor(defaultSelectedAccessor);
    } // reset to original value.
  }, [defaultSelectedAccessor, reportSectionId, selectedMetrics]);

  const items = useMemo(() => {
    if (metricData && metricData.rows.length > 0) {
      return mapMetricDataToListItems(
        labelAccessor,
        selectedValueAccessor,
        metricData,
        theme,
        labelCircleColor,
        rowFilter,
      );
    }

    if (isLoading) {
      return mockData;
    }

    return [];
  }, [labelAccessor, metricData, isLoading, selectedValueAccessor, rowFilter, labelCircleColor, theme]);

  const handleItemClick = (item: AnalyticsReportListItem, index: number) => {
    if (onItemClick) {
      return onItemClick(item, index);
    } // Leaves the possibility to override the default click handler (for Responses Time for instance).

    const subMetricLabel = metricData?.columns.find((c) => c.id === selectedValueAccessor)?.label;

    if (selectedValueAccessor === undefined || item.resourceId === undefined) {
      throw new Error(
        `Can't build valid contributing segment option from selectedValueAccessor=${selectedValueAccessor}, item.resourceId=${item.resourceId}`,
      );
    }

    return dispatch(
      setMetricDetailsAction(
        metric,
        {
          [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: {
            [ContributingSegmentsOptionsEnum.SUB_METRIC]: selectedValueAccessor,
            [ContributingSegmentsOptionsEnum.ROW]: item.resourceId,
          },
        },
        subMetricLabel && valueAccessors.length > 1 ? subMetricLabel : undefined,
      ),
    );
  };

  const isEmpty = !isLoading && isEmptyMetric(metricData);

  const renderList = () => {
    if (metricError) {
      return renderErrorState({metricError});
    }

    if (isEmpty) {
      return renderEmptyState({});
    }

    return shouldDisplayTops
      ? renderListSections(items, isLoading, labelAccessor, handleItemClick, onItemHover, hoveredItemId)
      : renderListItems(items, isLoading, labelAccessor, handleItemClick, onItemHover, hoveredItemId);
  };

  return (
    <ReportElementWrapper
      reportSectionId={reportSectionId}
      title={title}
      infoTooltip={infoTooltip}
      renderCustom={() =>
        valueAccessors.length > 1 && (
          <ReportListAccessorDropdown
            columns={metricData?.columns}
            valueAccessors={valueAccessors}
            selectedValueAccessor={selectedValueAccessor}
            onItemSelect={(selectedSubMetric) => updateSelectedMetrics([selectedSubMetric], reportSectionId)}
          />
        )
      }
      isExportable={isExportable}
      width={width}
      toggleExportDialog={toggleExportDialog}
    >
      {renderList()}
      {/* Export dialog popover */}
      {shouldShowExportDialog && toggleExportDialog && (
        <ChartExportViewDialog
          onRequestClose={toggleExportDialog}
          generateFormattedData={generateCsvFormattedData(metricData, items)}
          title={exportTitle ?? reportSectionId}
        />
      )}
    </ReportElementWrapper>
  );
};

/*
 * Helpers.
 */

const findColumn = (columns: ReadonlyArray<AnalyticsTableMetricColumn>, accessor: string) => {
  const columnIndex = columns.findIndex((c) => c.id === accessor);

  // If accessor was not found, return early.
  if (columnIndex === -1) {
    return undefined;
  }

  const column = columns[columnIndex];

  return {
    index: columnIndex,
    accessor: column.id,
    type: column.type,
    label: column.label,
  };
};

const mapMetricDataToListItems = (
  labelAccessor: string,
  valueAccessor: string | undefined,
  metricData: AnalyticsTableMetric,
  theme: UserTheme,
  labelCircleColor?: PaletteColorsEnum,
  shouldConsiderRow?: RowFilter,
): ReadonlyArray<AnalyticsReportListItem> => {
  // If valueAccessor is not defined, return early.
  if (!valueAccessor) {
    return [];
  }

  const labelColumn = findColumn(metricData.columns, labelAccessor);
  const valueColumn = findColumn(metricData.columns, valueAccessor);

  if (
    isUndefined(labelColumn) ||
    labelColumn.index === -1 ||
    isUndefined(valueColumn) ||
    valueColumn.index === -1
  ) {
    return [];
  }

  const maybeRenderVariation = (variation?: number) =>
    variation && isNumericDataPointType(valueColumn.type) ? (
      <>
        {variation > 0 && '+'}
        {getMetricRenderer(valueColumn.type)({value: variation})}
      </>
    ) : undefined;

  return (
    metricData.rows
      .filter((row) => (shouldConsiderRow ? shouldConsiderRow(row, metricData.columns, valueAccessor) : true))
      .map((row, index) => {
        const labelRow = row[labelColumn.index];
        const valueRow = row[valueColumn.index];

        const {variation, icon, color} = isNumericDataPoint(valueRow)
          ? getVariationConfig(valueRow.value, valueRow.previousValue, theme)
          : getVariationConfig(0, 0, theme);

        const res: AnalyticsReportListItem = {
          label: renderFormattedMetricData(labelRow, labelColumn.type),
          value: renderFormattedMetricData(valueRow, valueColumn.type),
          variation,
          formattedVariation: maybeRenderVariation(variation),
          labelCircleColor: labelCircleColor
            ? getDonutArcIndexColor(index, theme, labelCircleColor)
            : undefined,
          icon,
          color,
        };

        if (isResourceDataPoint(labelRow)) {
          res.resourceId = labelRow.id;
        } // Attaching the resource Id if it exists.

        return res;
      }) ?? []
  );
};

const renderListItems = (
  items: ReadonlyArray<AnalyticsReportListItem>,
  isLoading: boolean,
  labelAccessor: string,
  onItemClick?: (item: AnalyticsReportListItem, index: number) => void,
  onItemHover?: HandlerOf<ReactText | undefined>,
  hoveredItemId?: string,
  sectionTitle?: ReactNode,
) => {
  const handleHoverEvent = (index?: number) => {
    if (onItemHover) {
      onItemHover(index);
    }
  };

  return (
    <StyledItemsWrapper>
      {sectionTitle && <StyledSectionTitle>{sectionTitle}</StyledSectionTitle>}
      {items.map((item, index) => (
        <ReportListItem
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          onClick={() => onItemClick && onItemClick(item, index)}
          onFocus={() => handleHoverEvent(index)}
          onMouseEnter={() => handleHoverEvent(index)}
          onMouseLeave={() => handleHoverEvent(undefined)}
          isLoading={isLoading}
          isHovered={hoveredItemId === `${labelAccessor}-${index}`}
          {...item}
        />
      ))}
    </StyledItemsWrapper>
  );
};

const renderListSections = (
  items: ReadonlyArray<AnalyticsReportListItem>,
  isLoading: boolean,
  labelAccessor: string,
  onItemClick?: (item: AnalyticsReportListItem, index: number) => void,
  onItemHover?: HandlerOf<ReactText | undefined>,
  hoveredItemId?: string,
) => {
  // Order items.
  const orderedItems = [...items]
    .filter((item): item is AnalyticsReportListItem & {variation: number} => isDefined(item.variation))
    .sort((a, b) => b.variation - a.variation);

  // If the report is still loading, we simply display the mocked items.
  const positiveVariationItems = !isLoading
    ? orderedItems.filter((item) => item.variation && item.variation > 0)
    : items;
  const negativeVariationItems = !isLoading
    ? orderedItems.filter((item) => item.variation && item.variation < 0)
    : items;

  return (
    <>
      {renderListItems(
        positiveVariationItems.slice(0, MAX_ITEMS_IN_TOPS),
        isLoading,
        `${labelAccessor}-top`, // We need a suffix here otherwise the indices are not enough to know which row was selected.
        onItemClick,
        (i) => onItemHover && onItemHover(`top-${i}`),
        `${hoveredItemId}`,
        <Trans id="analytics-report-list-top-increases-title">Top Increases</Trans>,
      )}
      {renderListItems(
        negativeVariationItems.slice(
          Math.max(negativeVariationItems.length - MAX_ITEMS_IN_TOPS, 0),
          negativeVariationItems.length,
        ),
        isLoading,
        `${labelAccessor}-bottom`, // We need a suffix here otherwise the indices are not enough to know which row was selected.
        onItemClick,
        (i) => onItemHover && onItemHover(`bottom-${i}`),
        `${hoveredItemId}`,
        <Trans id="analytics-report-list-top-decreases-title">Top Decreases</Trans>,
      )}
    </>
  );
};

function generateCsvFormattedData(
  metricData: AnalyticsTableMetric | undefined,
  items: ReadonlyArray<AnalyticsReportListItem>,
) {
  return (separator: SeparatorsEnum) => {
    // no data, return emptry string on attempted export
    if (isUndefined(metricData)) {
      return '';
    }

    const computeQuoteValue = (value: AnalyticsDataPointValueType) => computeCsvQuote(value, separator);

    const result = [];
    const headers = metricData.columns.map((column) => computeQuoteValue(column.label));
    result.push(headers.join(separator));

    metricData.rows.forEach((row) => {
      result.push(row.map((item) => computeQuoteValue(item.value)).join(separator));
    });

    return result.join('\n');
  };
}
